import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { Download, FileText, Package, BookOpen, Loader2, CheckCircle, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

function downloadCSV(filename, rows) {
  const escape = (v) => {
    const s = String(v ?? "").replace(/"/g, '""');
    return `"${s}"`;
  };
  const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Hardcoded product data ──────────────────────────────────────────────────
const PRODUCTS = [
  {
    handle: "lctx-tire-changer",
    title: "LCTX Air-Powered Portable Tire Changer",
    vendor: "LCTX",
    type: "Tire Changer Machine",
    price: "599.00",
    currency: "AUD",
    sku: "LCTX-TC-001",
    inventory: "In Stock",
    description:
      "The LCTX portable pneumatic tire changer is Australia's #1 air-powered tire changing machine. No electricity needed — runs entirely on compressed air (8–10 Bar / 116–145 PSI). Handles 12–21 inch wheels with 2-ton torque power. Includes built-in bead breaker, mounting head, and locking cone. Perfect for mobile mechanics, workshops, and DIY enthusiasts. Free shipping across Australia. 1-year warranty.",
    image1: "https://res.cloudinary.com/dilx8zm2p/image/upload/v1759783025/1_iatrwm.jpg",
    image2: "https://res.cloudinary.com/dilx8zm2p/image/upload/v1759783025/2_iatrwm.jpg",
    tags: "tire changer, portable, pneumatic, air-powered, mobile mechanic, australia",
  },
  {
    handle: "lctx-bead-breaker-arm",
    title: "LCTX Bead Breaker Arm – Spare Part",
    vendor: "LCTX",
    type: "Spare Part",
    price: "89.00",
    currency: "AUD",
    sku: "LCTX-SP-BBA-001",
    inventory: "In Stock",
    description:
      "Genuine OEM replacement bead breaker arm for the LCTX Tire Changer. Heavy-duty steel construction for long-lasting performance. Compatible with all LCTX tire changer models. Direct drop-in replacement — no modifications needed. Includes all necessary mounting hardware. Backed by LCTX quality guarantee.",
    image1: "",
    image2: "",
    tags: "spare part, bead breaker arm, replacement, LCTX",
  },
  {
    handle: "lctx-hub-assembly",
    title: "LCTX Hub Assembly – Spare Part",
    vendor: "LCTX",
    type: "Spare Part",
    price: "129.00",
    currency: "AUD",
    sku: "LCTX-SP-HA-001",
    inventory: "In Stock",
    description:
      "Genuine OEM replacement hub assembly for the LCTX Tire Changer. Precision-engineered for optimal performance and longevity. Compatible with all LCTX tire changer models. Complete assembly with all components included. Professional-grade materials for commercial durability.",
    image1: "",
    image2: "",
    tags: "spare part, hub assembly, replacement, LCTX",
  },
];

// ── Hardcoded written content ─────────────────────────────────────────────────
const PAGES_CONTENT = [
  {
    page: "Shipping Policy",
    section: "Overview",
    content:
      "FREE SHIPPING on all LCTX Tire Changer orders across Australia. Orders processed and shipped within 24-48 hours (business days) after payment confirmation.",
  },
  {
    page: "Shipping Policy",
    section: "Delivery Times",
    content:
      "Major Cities (Sydney, Melbourne, Brisbane, Perth): 3-5 business days. Regional Areas: 5-7 business days. Remote Areas: 7-10 business days.",
  },
  {
    page: "Shipping Policy",
    section: "Coverage",
    content:
      "Ships to all Australian states and territories: NSW, VIC, QLD, WA, SA, TAS, ACT, NT. Currently Australia only — no international shipping.",
  },
  {
    page: "Warranty Policy",
    section: "Overview",
    content:
      "Every LCTX Tyre Changer comes with a 1-Year Manufacturer's Warranty from the date of purchase. Covers defects in materials and workmanship under normal use.",
  },
  {
    page: "Warranty Policy",
    section: "What's Covered",
    content:
      "Manufacturing defects in materials, workmanship defects in assembly, faulty components or parts, mechanical failures under normal use, structural integrity issues, pneumatic system malfunctions.",
  },
  {
    page: "Warranty Policy",
    section: "What's NOT Covered",
    content:
      "Damage from misuse/abuse/negligence, normal wear and tear, improper installation damage, use beyond rated capacity, modifications or alterations, accidents or external forces, commercial/industrial use, consumable parts (rubber seals).",
  },
  {
    page: "Refund Policy",
    section: "Overview",
    content:
      "30-day return policy from delivery date. Customer pays return shipping ($50-$150 AUD) for change-of-mind returns. LCTX covers return shipping for defective or wrong items.",
  },
  {
    page: "Refund Policy",
    section: "Refund Timeline",
    content:
      "Inspection: 2-3 business days. Refund approval: 1-2 business days. Refund in account: 5-10 business days. Processed to original payment method.",
  },
  {
    page: "Privacy Policy",
    section: "Data Collected",
    content:
      "Personal: name, email, phone, shipping/billing address, payment info (via Stripe/PayPal), order history. Automatic: IP address, browser type, device info, pages visited, cookies.",
  },
  {
    page: "Privacy Policy",
    section: "Data Use",
    content:
      "Process and fulfill orders, send confirmations and shipping updates, provide customer support, improve website, send marketing (with consent), detect fraud, comply with legal obligations.",
  },
  {
    page: "Privacy Policy",
    section: "Contact",
    content:
      "Email: hkjenholtd@gmail.com | Phone: +852-63613177 | Address: Unit 6, 41-43 Lexton Road, Box Hill North VIC 3129, Australia",
  },
  {
    page: "HowToUse",
    section: "Phase 1 – Bead Breaking",
    content:
      "Use the built-in bead breaker arm against tire sidewall near rim edge. Apply pneumatic pressure via foot pedal to push bead away from rim. Repeat on both sides.",
  },
  {
    page: "HowToUse",
    section: "Phase 1 – Lubrication",
    content:
      "Apply tire lubricant or soapy water generously to tire bead and rim edge. Reduces friction and prevents damage during removal.",
  },
  {
    page: "HowToUse",
    section: "Phase 1 – Mount Wheel",
    content:
      "Place wheel on center post. Secure using locking cone and nut. Position valve stem in front of operator.",
  },
  {
    page: "HowToUse",
    section: "Phase 1 – Remove Tire",
    content:
      "Position mounting head (duck head) against rim edge. Insert tire iron behind head. Rotate wheel via foot pedal/hand crank. Repeat for second bead.",
  },
  {
    page: "HowToUse",
    section: "Phase 2 – Install New Tire",
    content:
      "Re-lubricate new tire beads and rim. Place tire on rim. Use mounting head to press first bead onto rim while rotating. Apply downward pressure opposite mounting head for slack. Rotate until second bead is seated. Inflate to recommended pressure.",
  },
  {
    page: "HowToUse",
    section: "Safety",
    content:
      "Always wear safety glasses. Never force dry tire onto rim. Maintain 8-10 Bar (116-145 PSI). Inspect tires before mounting. Never exceed recommended inflation pressure during bead seating.",
  },
];

// ── Export button component ──────────────────────────────────────────────────
function ExportCard({ icon: Icon, title, description, onExport, loading, done }) {
  return (
    <Card className="border-2 border-gray-200 hover:border-red-300 transition-all">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-red-600" />
          </div>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-gray-600 text-sm mb-4">{description}</p>
        <Button
          onClick={onExport}
          disabled={loading}
          className="w-full bg-red-600 hover:bg-red-700 text-white gap-2"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : done ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {loading ? "Preparing..." : done ? "Downloaded!" : "Download CSV"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function DataExportPage() {
  const [loading, setLoading] = useState({});
  const [done, setDone] = useState({});

  const markLoading = (key) => setLoading((p) => ({ ...p, [key]: true }));
  const markDone = (key) => {
    setLoading((p) => ({ ...p, [key]: false }));
    setDone((p) => ({ ...p, [key]: true }));
    setTimeout(() => setDone((p) => ({ ...p, [key]: false })), 3000);
  };

  // ── Export: Blog Posts ──
  const exportBlogPosts = async () => {
    markLoading("blog");
    const posts = await base44.entities.BlogPost.list("-created_date", 200);
    const headers = ["Handle", "Title", "Excerpt", "Content", "Category", "Tags", "Author", "Status", "Published At"];
    const rows = [
      headers,
      ...posts.map((p) => [
        p.slug || "",
        p.title || "",
        p.excerpt || "",
        p.content || "",
        p.category || "",
        p.tags || "",
        p.author_name || "LCTX Team",
        p.status || "published",
        p.created_date ? new Date(p.created_date).toISOString().split("T")[0] : "",
      ]),
    ];
    downloadCSV("lctx_blog_posts.csv", rows);
    markDone("blog");
  };

  // ── Export: Products ──
  const exportProducts = () => {
    markLoading("products");
    const headers = ["Handle", "Title", "Vendor", "Type", "Price", "Currency", "SKU", "Inventory", "Description", "Image 1", "Image 2", "Tags"];
    const rows = [
      headers,
      ...PRODUCTS.map((p) => [
        p.handle, p.title, p.vendor, p.type, p.price, p.currency,
        p.sku, p.inventory, p.description, p.image1, p.image2, p.tags,
      ]),
    ];
    downloadCSV("lctx_products.csv", rows);
    markDone("products");
  };

  // ── Export: Pages Content ──
  const exportPages = () => {
    markLoading("pages");
    const headers = ["Page", "Section", "Content"];
    const rows = [headers, ...PAGES_CONTENT.map((r) => [r.page, r.section, r.content])];
    downloadCSV("lctx_pages_content.csv", rows);
    markDone("pages");
  };

  // ── Export: Newsletter Subscribers ──
  const exportSubscribers = async () => {
    markLoading("subscribers");
    const subs = await base44.entities.NewsletterSubscriber.list("-created_date", 500);
    const headers = ["Email", "Name", "Source", "Status", "Subscribed At"];
    const rows = [
      headers,
      ...subs.map((s) => [
        s.email || "",
        s.name || "",
        s.source || "",
        s.status || "active",
        s.created_date ? new Date(s.created_date).toISOString().split("T")[0] : "",
      ]),
    ];
    downloadCSV("lctx_newsletter_subscribers.csv", rows);
    markDone("subscribers");
  };

  // ── Export: Contact Messages ──
  const exportContacts = async () => {
    markLoading("contacts");
    const msgs = await base44.entities.ContactMessage.list("-created_date", 500);
    const headers = ["Name", "Email", "Phone", "Subject", "Message", "Status", "Date & Time"];
    const rows = [
      headers,
      ...msgs.map((m) => [
        m.name || "",
        m.email || "",
        m.phone || "",
        m.subject || "",
        m.message || "",
        m.status || "",
        m.created_date ? new Date(m.created_date).toISOString() : "",
      ]),
    ];
    downloadCSV("lctx_contact_messages.csv", rows);
    markDone("contacts");
  };

  // ── Export: Orders ──
  const exportOrders = async () => {
    markLoading("orders");
    const orders = await base44.entities.Order.list("-created_date", 1000);
    const headers = [
      "Order ID", "Customer Name", "Email", "Phone", "Shipping Address", "City",
      "State", "Zip", "Country", "Quantity", "Total Amount", "Order Status",
      "Payment Status", "Payment Method", "Order Notes", "Date & Time",
    ];
    const rows = [
      headers,
      ...orders.map((o) => [
        o.id || "",
        o.customer_name || "",
        o.email || "",
        o.phone || "",
        o.shipping_address || "",
        o.city || "",
        o.state || "",
        o.zip_code || "",
        o.country || "",
        o.quantity ?? "",
        o.total_amount ?? "",
        o.order_status || "",
        o.payment_status || "",
        o.payment_method || "",
        o.order_notes || "",
        o.created_date ? new Date(o.created_date).toISOString() : "",
      ]),
    ];
    downloadCSV("lctx_orders.csv", rows);
    markDone("orders");
  };

  // ── Export: SEO Meta Data ──
  const exportSEO = () => {
    markLoading("seo");
    const seoData = [
      { url: "/", shopify_page: "Homepage", title: "LCTX Tire Changer Machine | Air-Powered Portable Tyre Changer Australia", description: "LCTX Air-Powered Tire Changer - Australia's #1 portable tyre changing machine. No electricity needed, 12-21\" wheels, 2 ton torque. Free shipping. Buy now $699.", redirect_from: "/", redirect_to: "/" },
      { url: "/Product", shopify_page: "products/lctx-tire-changer", title: "LCTX Air-Powered Tire Changer | Professional Portable Tyre Changing Machine", description: "Professional LCTX pneumatic tire changer machine. Portable, air-powered design for mobile mechanics. Handles 12-21\" wheels. Order today with free Australia shipping.", redirect_from: "/Product", redirect_to: "/products/lctx-tire-changer" },
      { url: "/SpareParts", shopify_page: "collections/spare-parts", title: "LCTX Spare Parts | Replacement Parts for Tire Changer", description: "Genuine OEM replacement parts for LCTX tire changers. Bead breaker arm, hub assembly, and more. Fast shipping across Australia.", redirect_from: "/SpareParts", redirect_to: "/collections/spare-parts" },
      { url: "/HowToUse", shopify_page: "pages/how-to-use", title: "How To Use LCTX Tire Changer | Installation & Operation Guide", description: "Step-by-step guide on how to use the LCTX portable tire changer. Learn installation, operation, and best practices for efficient tire changing. Videos included.", redirect_from: "/HowToUse", redirect_to: "/pages/how-to-use" },
      { url: "/About", shopify_page: "pages/about", title: "About LCTX | Leading Tire Changer Manufacturer", description: "LCTX manufactures professional-grade portable tire changers. Learn about our pneumatic tyre changing solutions for mechanics and DIY enthusiasts.", redirect_from: "/About", redirect_to: "/pages/about" },
      { url: "/Blog", shopify_page: "blogs/news", title: "LCTX Blog | Tire Changing Tips & Industry News", description: "Expert tire changing tips, maintenance guides, and industry news from LCTX. Learn how to change tyres efficiently with our portable machines.", redirect_from: "/Blog", redirect_to: "/blogs/news" },
      { url: "/Contact", shopify_page: "pages/contact", title: "Contact LCTX | Tire Changer Support Australia", description: "Contact LCTX Australia for tire changer support, sales inquiries, and technical assistance. Email: eztirechanger@gmail.com, Phone: +852-63613177", redirect_from: "/Contact", redirect_to: "/pages/contact" },
      { url: "/Affiliate", shopify_page: "pages/affiliate", title: "LCTX Affiliate Program | Earn with Tire Changer Sales", description: "Join the LCTX Affiliate Program and earn commission selling portable tire changers. High conversion rates, competitive commissions, marketing support.", redirect_from: "/Affiliate", redirect_to: "/pages/affiliate" },
      { url: "/ShippingPolicy", shopify_page: "policies/shipping-policy", title: "Shipping Policy | LCTX Tire Changer Australia", description: "Free shipping on all LCTX Tire Changer orders across Australia. 3-10 business day delivery. Learn more about our shipping terms.", redirect_from: "/ShippingPolicy", redirect_to: "/policies/shipping-policy" },
      { url: "/WarrantyPolicy", shopify_page: "policies/warranty-policy", title: "Warranty Policy | 1-Year LCTX Tyre Changer Warranty", description: "Every LCTX Tyre Changer comes with a 1-year manufacturer's warranty. Learn what's covered and how to make a claim.", redirect_from: "/WarrantyPolicy", redirect_to: "/policies/warranty-policy" },
      { url: "/RefundPolicy", shopify_page: "policies/refund-policy", title: "Refund Policy | 30-Day Returns | LCTX Tire Changer", description: "30-day return policy for LCTX Tire Changers. Learn about our refund process, eligibility, and how to initiate a return.", redirect_from: "/RefundPolicy", redirect_to: "/policies/refund-policy" },
      { url: "/PrivacyPolicy", shopify_page: "policies/privacy-policy", title: "Privacy Policy | LCTX Tire Changer", description: "Learn how LCTX collects, uses, and protects your personal data. Compliant with Australian Privacy Act.", redirect_from: "/PrivacyPolicy", redirect_to: "/policies/privacy-policy" },
    ];
    const headers = ["Current URL", "Shopify Page", "SEO Title", "Meta Description", "301 Redirect From", "301 Redirect To"];
    const rows = [
      headers,
      ...seoData.map((r) => [r.url, r.shopify_page, r.title, r.description, r.redirect_from, r.redirect_to]),
    ];
    downloadCSV("lctx_seo_redirects.csv", rows);
    markDone("seo");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl font-black mb-2">Data Export</h1>
          <p className="text-red-100 text-lg">Download your LCTX data as CSV files — ready to import into Shopify or any platform.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Shopify note */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-8 text-sm text-blue-900">
          <strong>📌 Shopify Import Tips:</strong> For products, use <em>Shopify's product CSV importer</em>. For blog posts, use a Shopify blog import app (e.g. <em>Matrixify</em>). Policy pages can be copy-pasted directly into Shopify's <em>Online Store → Pages</em> section.
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <ExportCard
            icon={Package}
            title="Product Catalog"
            description="3 products: LCTX Tire Changer, Bead Breaker Arm, Hub Assembly — with titles, descriptions, prices, SKUs, and image URLs."
            onExport={exportProducts}
            loading={loading.products}
            done={done.products}
          />
          <ExportCard
            icon={BookOpen}
            title="Blog Posts"
            description="All blog posts with titles, slugs, excerpts, full content, categories, and tags — Shopify-ready."
            onExport={exportBlogPosts}
            loading={loading.blog}
            done={done.blog}
          />
          <ExportCard
            icon={FileText}
            title="Pages & Policies"
            description="Written content from Shipping, Warranty, Refund, Privacy policies and the How-To-Use guide — broken into sections."
            onExport={exportPages}
            loading={loading.pages}
            done={done.pages}
          />
          <ExportCard
            icon={FileText}
            title="Newsletter Subscribers"
            description="All newsletter subscribers with email, name, source, status, and subscription date."
            onExport={exportSubscribers}
            loading={loading.subscribers}
            done={done.subscribers}
          />
          <ExportCard
            icon={Package}
            title="Orders"
            description="All orders with customer details, shipping address, quantity, total, status, payment, and date & time."
            onExport={exportOrders}
            loading={loading.orders}
            done={done.orders}
          />
          <ExportCard
            icon={FileText}
            title="Contact Messages"
            description="All contact form submissions with name, email, phone, subject, message, status, and date & time."
            onExport={exportContacts}
            loading={loading.contacts}
            done={done.contacts}
          />
          <ExportCard
            icon={Search}
            title="SEO Meta Data & 301 Redirects"
            description="All page SEO titles, meta descriptions, and a redirect map from current URLs to Shopify URLs — import directly into Shopify's URL Redirects."
            onExport={exportSEO}
            loading={loading.seo}
            done={done.seo}
          />
        </div>

        <p className="text-center text-gray-400 text-sm mt-10">
          ⚠️ Customer reviews are not stored in this app. Export them directly from Google Business, Facebook, or Trustpilot.
        </p>
      </div>
    </div>
  );
}